import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  ChevronDown,
  ChevronRight,
  Search,
  Eye,
  EyeOff,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { PlatformCategoryApi } from "@/api/platformCategory.api";
import { BrandApi } from "@/api/brand.api";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { ProductApi } from "@/api/product.api";
import { PaginationControls } from "@/components/common/PaginationControl";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { getFormattedDateTime } from "@/utils";

// Dummy data for categories and products
const initialCategories = [
  {
    id: "cat1",
    name: "Entertainment",
    hidden: false,
    children: [
      { id: "cat1-1", name: "Movies", hidden: false, children: [] },
      { id: "cat1-2", name: "Events", hidden: false, children: [] },
    ],
  },
  {
    id: "cat2",
    name: "Retail",
    hidden: false,
    children: [],
  },
  {
    id: "cat3",
    name: "Food & Dining",
    hidden: false,
    children: [
      { id: "cat3-1", name: "Restaurants", hidden: false, children: [] },
      { id: "cat3-2", name: "Cafes", hidden: false, children: [] },
    ],
  },
  {
    id: "cat4",
    name: "Travel",
    hidden: false,
    children: [],
  },
];

const initialProducts = [
  {
    id: "prod1",
    name: "Avaline Wine Gift Card",
    brand: "Avaline",
    merchant: "Demo-Merchant-1",
    shippedFrom: "United Arab Emirates",
    image: "/placeholder.svg",
    categoryId: "cat3",
  },
  {
    id: "prod2",
    name: "Total Wine Gift Card",
    brand: "Total Wine",
    merchant: "Demo-Merchant-1",
    shippedFrom: "United Arab Emirates",
    image: "/placeholder.svg",
    categoryId: "cat3",
  },
];

const PLACEHOLDER_IMAGE = "/placeholder.svg";

function flattenCategories(categories) {
  let result = [];
  for (const cat of categories) {
    result.push({ id: cat.id, name: cat.name });
    if (cat.children && cat.children.length > 0) {
      result = result.concat(flattenCategories(cat.children));
    }
  }
  return result;
}

function renderCategoryAccordion(
  categories,
  onSelect,
  selectedId,
  onDelete,
  onToggleHide,
  setUserSelectedCategory,
  onAddSubCategory,
  level = 0
) {
  // If no categories or all categories have no children, render as a flat list
  if (!categories.some((cat) => cat.children && cat.children.length > 0)) {
    return (
      <div className="flex flex-col gap-1">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className={`flex items-center px-2 py-1${
              cat.hidden ? " opacity-50" : ""
            }${level > 0 ? ` ml-${Math.min(level * 6, 12)}` : ""}`}
          >
            <span className="flex items-center gap-2">
              {cat.name}
              <input
                type="radio"
                name="platform-category-radio"
                checked={selectedId === cat.id}
                onChange={(e) => {
                  onSelect(cat.id);
                  setUserSelectedCategory(true);
                }}
                onClick={(e) => e.stopPropagation()}
                className="ml-2"
              />
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="p-1 ml-2">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    console.log(
                      "Dropdown menu item clicked for category:",
                      cat
                    );
                    e.stopPropagation();
                    onToggleHide(cat);
                  }}
                >
                  {cat.hidden ? (
                    <>
                      <Eye className="w-4 h-4 mr-2" /> Mark as unhidden
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4 mr-2" /> Mark as hidden
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddSubCategory(cat);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" /> Add sub-category
                </DropdownMenuItem>
                {cat.categoryCode !== "ROOT" && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(cat);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2 text-red-500" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>
    );
  }
  // Otherwise, render as accordion
  return (
    <Accordion type="multiple" className="w-full">
      {categories.map((cat) => (
        <AccordionItem
          key={cat.id}
          value={cat.id}
          className={cat.hidden ? "opacity-50" : undefined}
        >
          <AccordionTrigger
            className={`flex items-center gap-2 px-2 py-1 ${
              level > 0 ? `ml-${Math.min(level * 6, 12)}` : ""
            }`}
          >
            <div className="flex items-center flex-1 justify-between w-full">
              <span className="flex items-center gap-2">
                {cat.name}
                <input
                  type="radio"
                  name="platform-category-radio"
                  checked={selectedId === cat.id}
                  onChange={(e) => {
                    onSelect(cat.id);
                    setUserSelectedCategory(true);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="ml-2"
                />
              </span>
              <div className="flex items-center gap-1 ml-auto">
                {/* Accordion arrow is rendered by AccordionTrigger by default at the end */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="p-1 ml-2">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        console.log(
                          "Accordion dropdown menu item clicked for category:",
                          cat
                        );
                        e.stopPropagation();
                        onToggleHide(cat);
                      }}
                    >
                      {cat.hidden ? (
                        <>
                          <Eye className="w-4 h-4 mr-2" /> Mark as unhidden
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" /> Mark as hidden
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddSubCategory(cat);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add sub-category
                    </DropdownMenuItem>
                    {cat.categoryCode !== "ROOT" && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(cat);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2 text-red-500" /> Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {cat.children.length > 0 &&
              renderCategoryAccordion(
                cat.children,
                onSelect,
                selectedId,
                onDelete,
                onToggleHide,
                setUserSelectedCategory,
                onAddSubCategory,
                level + 1
              )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function findAndUpdateCategory(categories, id, updater) {
  return categories.map((cat) => {
    if (cat.id === id) {
      return updater(cat);
    } else if (cat.children && cat.children.length > 0) {
      return {
        ...cat,
        children: findAndUpdateCategory(cat.children, id, updater),
      };
    } else {
      return cat;
    }
  });
}

function findAndRemoveCategory(categories, id) {
  let removed = null;
  const filtered = categories
    .filter((cat) => {
      if (cat.id === id) {
        removed = cat;
        return false;
      }
      return true;
    })
    .map((cat) => ({
      ...cat,
      children: cat.children
        ? findAndRemoveCategory(cat.children, id).categories
        : [],
    }));
  if (!removed) {
    for (const cat of categories) {
      if (cat.children && cat.children.length > 0) {
        const result = findAndRemoveCategory(cat.children, id);
        if (result.removed) {
          removed = result.removed;
          cat.children = result.categories;
        }
      }
    }
  }
  return { categories: filtered, removed };
}

function insertCategory(categories, parentId, newCat) {
  if (!parentId) {
    return [...categories, newCat];
  }
  return categories.map((cat) => {
    if (cat.id === parentId) {
      return { ...cat, children: [...cat.children, newCat] };
    } else if (cat.children && cat.children.length > 0) {
      return {
        ...cat,
        children: insertCategory(cat.children, parentId, newCat),
      };
    } else {
      return cat;
    }
  });
}

function isDescendant(categories, id, targetId) {
  if (id === targetId) return true;
  for (const cat of categories) {
    if (cat.id === targetId) {
      if (cat.children && cat.children.length > 0) {
        if (cat.children.some((child) => isDescendant([child], id, child.id)))
          return true;
      }
    } else if (cat.children && cat.children.length > 0) {
      if (isDescendant(cat.children, id, targetId)) return true;
    }
  }
  return false;
}

function findParentId(categories, id, parentId = "") {
  for (const cat of categories) {
    if (cat.id === id) return parentId;
    if (cat.children && cat.children.length > 0) {
      const found = findParentId(cat.children, id, cat.id);
      if (found) return found;
    }
  }
  return "";
}

function mapApiCategoryToLocal(cat) {
  return {
    id: cat.id,
    name: cat.categoryName,
    categoryCode: cat.categoryCode,
    hidden: cat.isHidden,
    children: (cat.subCategoryVOs || []).map(mapApiCategoryToLocal),
  };
}

const PlatformCategories = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [search, setSearch] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [parentCategoryId, setParentCategoryId] = useState("root");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [merchantFilter, setMerchantFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(true);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categoryProductsLoading, setCategoryProductsLoading] = useState(false);
  const [userSelectedCategory, setUserSelectedCategory] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  const [assignProductDialogOpen, setAssignProductDialogOpen] = useState(false);
  const [assignProductSearch, setAssignProductSearch] = useState("");
  const [assignProductMerchant, setAssignProductMerchant] = useState("");
  const [assignProductBrand, setAssignProductBrand] = useState("");
  const [assignProductCode, setAssignProductCode] = useState("");
  const [assignProductPage, setAssignProductPage] = useState(0);
  const [assignProductPageSize, setAssignProductPageSize] = useState(10);
  const [assignProductTotal, setAssignProductTotal] = useState(0);
  const [assignProductLoading, setAssignProductLoading] = useState(false);
  const [assignProductRows, setAssignProductRows] = useState([]);
  const [assignProductSelected, setAssignProductSelected] = useState([]);
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showAddSubCategoryDialog, setShowAddSubCategoryDialog] =
    useState(false);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [subCategoryName, setSubCategoryName] = useState("");
  const [subCategoryCode, setSubCategoryCode] = useState("");
  const [subCategoryDescription, setSubCategoryDescription] = useState("");
  const [isSubmittingSubCategory, setIsSubmittingSubCategory] = useState(false);

  // Find selected brand and merchant objects for filter mapping
  const selectedBrand = brands.find((b) => b.id === Number(brandFilter));
  const selectedMerchant = merchants.find(
    (m) => m.id === Number(merchantFilter)
  );

  useEffect(() => {
    async function fetchCategories() {
      setLoading(true);
      try {
        const { data } = await PlatformCategoryApi.fetchCategoryTree();
        if (data) {
          // Create a structure that includes the ROOT category with its sub-categories
          const rootCategory = mapApiCategoryToLocal(data);
          setCategories([rootCategory]);
        } else {
          setCategories([]);
        }
      } catch {
        setCategories([]);
      } finally {
        setLoading(false);
      }
    }
    fetchCategories();
  }, []);

  useEffect(() => {
    async function fetchBrands() {
      setBrandsLoading(true);
      try {
        const { data } = await BrandApi.fetchAllBrands();
        setBrands(
          (data || []).map((b) => ({
            value: b.id,
            label: b.brandName,
            id: b.id,
            name: b.brandName,
          }))
        );
      } catch {
        setBrands([]);
      } finally {
        setBrandsLoading(false);
      }
    }
    fetchBrands();
  }, []);

  useEffect(() => {
    async function fetchMerchants() {
      setMerchantsLoading(true);
      try {
        const { data } =
          await RedemptionMerchantApi.fetchAllRedemptionMerchants();
        setMerchants(
          (data?.redemptionMerchantVOS || []).map((m) => ({
            value: m.id,
            label: m.merchantName,
            id: m.id,
            name: m.merchantName,
          }))
        );
      } catch {
        setMerchants([]);
      } finally {
        setMerchantsLoading(false);
      }
    }
    fetchMerchants();
  }, []);

  useEffect(() => {
    // Only fetch if both filters are selected or searchText is not empty
    if (!(selectedBrand && selectedMerchant) && !searchText) {
      setProducts([]);
      setProductsLoading(false);
      return;
    }
    async function fetchProducts() {
      setProductsLoading(true);
      try {
        const { data } = await ProductApi.fetchGiftCardProducts({
          brandId: selectedBrand ? selectedBrand.id : null,
          redemptionMerchantId: selectedMerchant ? selectedMerchant.id : null,
          productName: searchText || "",
          productCode: null,
          productType: null,
          countryId: null,
          platformCategoryId: null,
          programCategoryId: null,
          pageNumber: 0,
          pageSize: 100,
          sortBy: "createdDate",
          sortOrder: "desc",
        });
        setProducts(
          (data?.giftCardProductVOs || []).map((p) => ({
            id: p.id,
            name: p.productName,
            brand: p.brandVO?.brandName || "",
            merchant: p.redemptionMerchantVO?.merchantName || "",
            shippedFrom: p.redemptionMerchantVO?.countryVO?.name || "",
            image: p.productImageUri || "/placeholder.svg",
            categoryId:
              (p.platformCategoryIds && p.platformCategoryIds[0]) || "",
          }))
        );
      } catch {
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    }
    fetchProducts();
  }, [brandFilter, merchantFilter, searchText]);

  useEffect(() => {
    if (!selectedCategoryId || !userSelectedCategory) {
      setCategoryProducts([]);
      setCategoryProductsLoading(false);
      setTotalProducts(0);
      return;
    }
    setCategoryProductsLoading(true);
    ProductApi.fetchGiftCardProducts({
      brandId: selectedBrand ? selectedBrand.id : null,
      redemptionMerchantId: selectedMerchant ? selectedMerchant.id : null,
      productName: searchText || "",
      productCode: null,
      productType: null,
      countryId: null,
      platformCategoryId: selectedCategoryId,
      programCategoryId: null,
      pageNumber: currentPage,
      pageSize: pageSize,
      sortBy: "createdDate",
      sortOrder: "desc",
    })
      .then(({ data }) => {
        setCategoryProducts(
          (data?.giftCardProductVOs || []).map((p) => ({
            id: p.id,
            name: p.productName,
            brand: p.brandVO?.brandName || "",
            merchant: p.redemptionMerchantVO?.merchantName || "",
            shippedFrom: p.redemptionMerchantVO?.countryVO?.name || "",
            image: p.productImageUri || "/placeholder.svg",
            categoryId:
              (p.platformCategoryIds && p.platformCategoryIds[0]) || "",
          }))
        );
        setTotalProducts(data?.totalRecords || 0);
      })
      .catch(() => {
        setCategoryProducts([]);
        setTotalProducts(0);
      })
      .finally(() => setCategoryProductsLoading(false));
  }, [
    selectedCategoryId,
    brandFilter,
    merchantFilter,
    searchText,
    userSelectedCategory,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    if (!assignProductDialogOpen) return;
    setAssignProductLoading(true);
    ProductApi.fetchGiftCardProducts({
      productName: assignProductSearch || null,
      productCode: assignProductCode || null,
      redemptionMerchantId:
        assignProductMerchant && assignProductMerchant !== "all"
          ? Number(assignProductMerchant)
          : null,
      brandId:
        assignProductBrand && assignProductBrand !== "all"
          ? Number(assignProductBrand)
          : null,
      countryId: null,
      platformCategoryId: null,
      programCategoryId: null,
      productType: null,
      pageNumber: assignProductPage,
      pageSize: assignProductPageSize,
      sortOrder: "desc",
      sortBy: "createdDate",
    })
      .then(({ data }) => {
        setAssignProductRows(data?.giftCardProductVOs || []);
        setAssignProductTotal(data?.totalRecords || 0);
      })
      .catch(() => {
        setAssignProductRows([]);
        setAssignProductTotal(0);
      })
      .finally(() => setAssignProductLoading(false));
  }, [
    assignProductDialogOpen,
    assignProductSearch,
    assignProductMerchant,
    assignProductBrand,
    assignProductCode,
    assignProductPage,
    assignProductPageSize,
  ]);

  // Handlers for category CRUD
  const handleAddCategory = () => {
    setShowAddDialog(true);
    setCategoryName("");
    setParentCategoryId("root");
    setError("");
  };
  const handleEditCategory = (cat) => {
    setEditCategory(cat);
    setCategoryName(cat.name);
    const parentId = findParentId(categories, cat.id);
    setParentCategoryId(parentId ? parentId : "root");
    setShowEditDialog(true);
    setError("");
  };
  const handleDeleteCategory = (cat) => {
    setCategoryToDelete(cat);
    setShowDeleteDialog(true);
  };
  const handleToggleHide = (cat) => {
    console.log("handleToggleHide called with category:", cat);
    if (!cat?.id) {
      console.log("No category ID found");
      return;
    }

    // Determine which API to call based on current state
    const isCurrentlyHidden = cat.isHidden || cat.hidden;
    const apiMethod = isCurrentlyHidden
      ? PlatformCategoryApi.unHidePlatformCategory(cat.id)
      : PlatformCategoryApi.hidePlatformCategory(cat.id);

    console.log(
      `Making API call to ${
        isCurrentlyHidden ? "unHide" : "hide"
      } platform category with ID:`,
      cat.id
    );

    apiMethod
      .then((response) => {
        console.log("API call successful:", response);
        const action = isCurrentlyHidden ? "unhidden" : "hidden";
        toast.success(`Category ${action} successfully`);

        // Refresh categories from API to get updated state
        (async function fetchCategories() {
          setLoading(true);
          try {
            const { data } = await PlatformCategoryApi.fetchCategoryTree();
            if (data) {
              const rootCategory = mapApiCategoryToLocal(data);
              setCategories([rootCategory]);
            } else {
              setCategories([]);
            }
          } catch (error) {
            console.error("Error fetching categories:", error);
            setCategories([]);
          } finally {
            setLoading(false);
          }
        })();
      })
      .catch((error) => {
        console.log("API call failed:", error);
        const action = isCurrentlyHidden ? "unhide" : "hide";
        toast.error(`Failed to ${action} category`);
      });
  };
  const handleAddSubCategory = (parentCategory) => {
    setSelectedParentCategory(parentCategory);
    setSubCategoryName("");
    setSubCategoryCode("");
    setSubCategoryDescription("");
    setShowAddSubCategoryDialog(true);
  };
  const handleSaveCategory = () => {
    if (!categoryName.trim()) {
      setError("Category name is required");
      return;
    }
    const parentId = parentCategoryId === "root" ? "" : parentCategoryId;
    if (showAddDialog) {
      // Prevent duplicate names at same level
      const allNames = flattenCategories(categories).map((c) =>
        c.name.toLowerCase()
      );
      if (allNames.includes(categoryName.trim().toLowerCase())) {
        setError("Category name already exists");
        return;
      }
      // Build payload for API
      const payload = {
        categoryCode: categoryName.trim().toUpperCase().replace(/\s+/g, "_"),
        categoryName: categoryName.trim(),
        categoryDescription: "", // Optionally add a description input
        parentCategoryId: parentId ? Number(parentId) : null,
        subCategoryVOs: [],
        programId: null,
      };
      PlatformCategoryApi.createPlatformCategory(payload)
        .then(() => {
          toast.success("Category added successfully");
          setShowAddDialog(false);
          setCategoryName("");
          setParentCategoryId("root");
          setError("");
          // Refresh categories from API
          (async function fetchCategories() {
            setLoading(true);
            try {
              const { data } = await PlatformCategoryApi.fetchCategoryTree();
              if (data && data.subCategoryVOs) {
                const mapped = (data.subCategoryVOs || []).map(
                  mapApiCategoryToLocal
                );
                setCategories(mapped);
              } else {
                setCategories([]);
              }
            } catch {
              setCategories([]);
            } finally {
              setLoading(false);
            }
          })();
        })
        .catch(() => {
          setError("Failed to add category");
        });
    } else if (showEditDialog && editCategory) {
      // Prevent moving under itself or descendant
      if (
        parentId &&
        (editCategory.id === parentId ||
          isDescendant([editCategory], parentId, editCategory.id))
      ) {
        setError("Cannot move category under itself or its descendant");
        return;
      }
      // Remove from old location
      const { categories: catsWithout, removed } = findAndRemoveCategory(
        categories,
        editCategory.id
      );
      // Insert at new location
      const updatedCat = {
        ...removed,
        name: categoryName.trim(),
        hidden: editCategory.hidden,
      };
      setCategories(insertCategory(catsWithout, parentId, updatedCat));
      setShowEditDialog(false);
      setEditCategory(null);
      setCategoryName("");
      setParentCategoryId("root");
      setError("");
    }
  };

  // Product assignment logic
  const handleRemoveProduct = (productId) => {
    setProducts(
      products.map((p) => (p.id === productId ? { ...p, categoryId: null } : p))
    );
  };
  const handleMoveProduct = (productId, newCategoryId) => {
    setProducts(
      products.map((p) =>
        p.id === productId ? { ...p, categoryId: newCategoryId } : p
      )
    );
  };

  // Add a reset handler
  const handleResetFilters = () => {
    setBrandFilter("");
    setMerchantFilter("");
    setSearchText("");
    setCurrentPage(0);
    setPageSize(10);
  };

  // Reset handler for assign dialog
  const handleAssignProductReset = () => {
    setAssignProductSearch("");
    setAssignProductMerchant("");
    setAssignProductBrand("");
    setAssignProductCode("");
    setAssignProductPage(0);
    setAssignProductPageSize(5);
    setAssignProductSelected([]);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete?.id) return;
    try {
      await PlatformCategoryApi.deletePlatformCategory(categoryToDelete.id);
      toast.success("Category deleted successfully");
      setShowDeleteDialog(false);
      setCategoryToDelete(null);
      // Refresh categories from API
      setLoading(true);
      try {
        const { data } = await PlatformCategoryApi.fetchCategoryTree();
        if (data && data.subCategoryVOs) {
          const mapped = (data.subCategoryVOs || []).map(mapApiCategoryToLocal);
          setCategories(mapped);
        } else {
          setCategories([]);
        }
      } catch {
        setCategories([]);
      } finally {
        setLoading(false);
      }
    } catch {
      toast.error("Failed to delete category");
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Platform Categories
        </h1>
        <p className="text-gray-500">
          Manage your platform categories and assign products to them.
        </p>
      </div>
      <div className="flex flex-col md:flex-row gap-6 h-full min-h-[700px] items-stretch">
        {/* Category Tree Panel */}
        <Card className="w-full md:w-1/3 max-w-xs min-w-[280px] h-full min-h-[700px]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold">
              Platform Categories
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col h-full">
            <Input
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-3"
            />
            <div className="flex-1 min-h-0">
              {loading ? (
                <div className="text-center text-gray-500 py-8">Loading...</div>
              ) : (
                renderCategoryAccordion(
                  categories,
                  setSelectedCategoryId,
                  selectedCategoryId,
                  handleDeleteCategory,
                  handleToggleHide,
                  setUserSelectedCategory,
                  handleAddSubCategory
                )
              )}
            </div>
          </CardContent>
        </Card>

        {/* Product Assignment Panel */}
        <div className="flex flex-col flex-1 h-full">
          <Card className="flex-1 h-full min-h-[700px]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold">
                Assign Products
              </CardTitle>
              <Button
                onClick={() => {
                  if (!selectedCategoryId) {
                    toast.error("Please select a category first.");
                    return;
                  }
                  setAssignProductDialogOpen(true);
                }}
                disabled={!selectedCategoryId}
                title={
                  !selectedCategoryId
                    ? "Select a category to assign products"
                    : undefined
                }
              >
                Assign Product
              </Button>
            </CardHeader>
            <CardContent className="flex flex-col h-full">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Input
                  placeholder="Search by name"
                  className="flex-1"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Select value={brandFilter} onValueChange={setBrandFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Select Brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brandsLoading ? (
                      <div className="px-3 py-2 text-gray-500">Loading...</div>
                    ) : brands.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500">No brands</div>
                    ) : (
                      brands.map((brand) => (
                        <SelectItem key={brand.id} value={brand.id.toString()}>
                          {brand.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Select
                  value={merchantFilter}
                  onValueChange={setMerchantFilter}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select Merchant" />
                  </SelectTrigger>
                  <SelectContent>
                    {merchantsLoading ? (
                      <div className="px-3 py-2 text-gray-500">Loading...</div>
                    ) : merchants.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500">
                        No merchants
                      </div>
                    ) : (
                      merchants.map((merchant) => (
                        <SelectItem
                          key={merchant.id}
                          value={merchant.id.toString()}
                        >
                          {merchant.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={handleResetFilters}
                >
                  Reset
                </Button>
              </div>
              <div className="mb-2 font-semibold text-gray-700">
                Category:{" "}
                <Badge>
                  {flattenCategories(categories).find(
                    (c) => c.id === selectedCategoryId
                  )?.name || "-"}
                </Badge>
              </div>
              <div className="flex-1 min-h-0 flex flex-col mb-5">
                <DataTable
                  title=""
                  data={categoryProductsLoading ? [] : categoryProducts}
                  columns={[
                    {
                      header: "Product Image",
                      cell: (row) => (
                        <img
                          src={row.image || PLACEHOLDER_IMAGE}
                          alt={row.name}
                          className="w-12 h-12 object-contain rounded border bg-gray-50"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = PLACEHOLDER_IMAGE;
                          }}
                        />
                      ),
                    },
                    { header: "Product Name", cell: (row) => row.name },
                    { header: "Brand", cell: (row) => row.brand },
                    { header: "Merchant", cell: (row) => row.merchant },
                    { header: "Shipped From", cell: (row) => row.shippedFrom },
                    {
                      header: "Action",
                      cell: (row) => (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="p-1">
                              <MoreVertical className="w-5 h-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleRemoveProduct(row.id)}
                            >
                              Remove from category
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <div className="w-full">
                                <Select
                                  onValueChange={(catId) =>
                                    handleMoveProduct(row.id, catId)
                                  }
                                >
                                  <SelectTrigger className="w-full text-left px-0 border-none shadow-none focus:ring-0 focus:border-none">
                                    <SelectValue placeholder="Move to..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {flattenCategories(categories)
                                      .filter(
                                        (c) => c.id !== selectedCategoryId
                                      )
                                      .map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                          {c.name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ),
                    },
                  ]}
                />
              </div>
              {categoryProducts && categoryProducts.length > 0 && (
                <div className="flex items-center justify-between mt-4 flex-shrink-0 w-full self-end mt-auto mt-5">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      Rows per page:
                    </span>
                    <Select
                      value={pageSize.toString()}
                      onValueChange={(value) => {
                        setPageSize(Number(value));
                        setCurrentPage(0);
                      }}
                    >
                      <SelectTrigger className="w-[80px]">
                        <SelectValue placeholder={pageSize.toString()} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <PaginationControls
                    totalPages={Math.ceil(totalProducts / pageSize)}
                    currentPage={currentPage}
                    onNextHandler={() =>
                      setCurrentPage((p) =>
                        Math.min(Math.ceil(totalProducts / pageSize) - 1, p + 1)
                      )
                    }
                    onPrevHandler={() =>
                      setCurrentPage((p) => Math.max(0, p - 1))
                    }
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Add/Edit Category Dialog */}
        <Dialog
          open={showAddDialog || showEditDialog}
          onOpenChange={(v) => {
            setShowAddDialog(false);
            setShowEditDialog(false);
            setError("");
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {showAddDialog ? "Add Category" : "Edit Category"}
              </DialogTitle>
            </DialogHeader>
            <div className="mb-2 font-medium text-gray-700">Category Name</div>
            <Input
              placeholder="Category Name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              className="mb-4"
            />
            <div className="mb-2 font-medium text-gray-700">
              Parent Category
            </div>
            <Select
              value={parentCategoryId}
              onValueChange={setParentCategoryId}
            >
              <SelectTrigger className="w-full mb-4">
                <SelectValue placeholder="Select Parent Category (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="root">None (Root)</SelectItem>
                {flattenCategories(categories)
                  .filter((c) => !editCategory || c.id !== editCategory.id)
                  .map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddDialog(false);
                  setShowEditDialog(false);
                  setError("");
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveCategory}>
                {showAddDialog ? "Add" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Product Dialog (moved to root) */}
        <Dialog
          open={assignProductDialogOpen}
          onOpenChange={setAssignProductDialogOpen}
        >
          <DialogContent className="max-w-5xl w-full p-0 rounded-2xl shadow-xl border bg-white">
            <div className="rounded-2xl overflow-hidden">
              {/* Confirmation bar */}
              {assignProductSelected.length > 0 && showAssignConfirm && (
                <div className="flex items-center justify-between px-6 py-3 bg-gray-100 border-b border-gray-200 rounded-t-2xl">
                  <span className="font-semibold text-sm text-gray-800">
                    Are you sure you want to assign these products to category{" "}
                    {selectedCategoryId}?
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      disabled={assigning}
                      onClick={async () => {
                        if (!selectedCategoryId) {
                          toast.error("Please select a category first.");
                          return;
                        }
                        setAssigning(true);
                        try {
                          const categoryIdNum = Number(selectedCategoryId);
                          if (!categoryIdNum) {
                            toast.error("Invalid category selected.");
                            setAssigning(false);
                            return;
                          }
                          const payload = assignProductSelected.map((pid) => ({
                            platformCategoryVO: {
                              id: categoryIdNum,
                              programId: 1,
                            },
                            giftCardProductVO: { id: pid },
                          }));
                          await PlatformCategoryApi.assignProductsToPlatformCategory(
                            payload
                          );
                          // Refetch products for the selected category and update the grid
                          setCategoryProductsLoading(true);
                          try {
                            const { data } =
                              await ProductApi.fetchGiftCardProducts({
                                brandId: selectedBrand
                                  ? selectedBrand.id
                                  : null,
                                redemptionMerchantId: selectedMerchant
                                  ? selectedMerchant.id
                                  : null,
                                productName: searchText || "",
                                productCode: null,
                                productType: null,
                                countryId: null,
                                platformCategoryId: categoryIdNum,
                                programCategoryId: null,
                                pageNumber: currentPage,
                                pageSize: pageSize,
                                sortBy: "createdDate",
                                sortOrder: "desc",
                              });
                            setCategoryProducts(
                              (data?.giftCardProductVOs || []).map((p) => ({
                                id: p.id,
                                name: p.productName,
                                brand: p.brandVO?.brandName || "",
                                merchant:
                                  p.redemptionMerchantVO?.merchantName || "",
                                shippedFrom:
                                  p.redemptionMerchantVO?.countryVO?.name || "",
                                image: p.productImageUri || "/placeholder.svg",
                                categoryId:
                                  (p.platformCategoryIds &&
                                    p.platformCategoryIds[0]) ||
                                  "",
                              }))
                            );
                            setTotalProducts(data?.totalRecords || 0);
                          } catch {
                            setCategoryProducts([]);
                            setTotalProducts(0);
                          } finally {
                            setCategoryProductsLoading(false);
                          }
                          toast.success("Products assigned successfully");
                          setAssignProductDialogOpen(false);
                          setAssignProductSelected([]);
                          setShowAssignConfirm(false);
                        } catch (err) {
                          toast.error("Failed to assign products");
                        } finally {
                          setAssigning(false);
                        }
                      }}
                      aria-label="Confirm"
                    >
                      ✓
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-gray-500 hover:text-red-500 text-xl"
                      onClick={() => setShowAssignConfirm(false)}
                      aria-label="Cancel"
                    >
                      ×
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
                <h2 className="text-lg font-bold text-gray-800">
                  Choose Product
                </h2>
                <Button
                  variant="ghost"
                  className="text-gray-500 hover:text-red-500 text-2xl px-2 py-0"
                  onClick={() => setAssignProductDialogOpen(false)}
                  aria-label="Close"
                >
                  ×
                </Button>
              </div>
              {/* Filters row */}
              <div className="flex flex-wrap gap-2 mb-4 items-end px-6 pt-4">
                <Input
                  placeholder="Search"
                  value={assignProductSearch}
                  onChange={(e) => setAssignProductSearch(e.target.value)}
                  className="w-48"
                />
                <Input
                  placeholder="Product code"
                  value={assignProductCode}
                  onChange={(e) => setAssignProductCode(e.target.value)}
                  className="w-40"
                />
                <Select
                  value={assignProductMerchant}
                  onValueChange={setAssignProductMerchant}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select merchant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All merchants</SelectItem>
                    {merchants.map((merchant) => (
                      <SelectItem
                        key={merchant.id}
                        value={merchant.id.toString()}
                      >
                        {merchant.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={assignProductBrand}
                  onValueChange={setAssignProductBrand}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All brands</SelectItem>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id.toString()}>
                        {brand.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" onClick={handleAssignProductReset}>
                  Reset
                </Button>
              </div>
              {/* Table */}
              <div className="overflow-x-auto px-6 pb-2">
                <table className="min-w-full border rounded-xl overflow-hidden bg-white">
                  <thead>
                    <tr className="text-left text-gray-600 text-xs border-b bg-gray-50">
                      <th className="py-2 px-2">
                        <input
                          type="checkbox"
                          checked={
                            assignProductSelected.length ===
                              assignProductRows.length &&
                            assignProductRows.length > 0
                          }
                          onChange={(e) =>
                            setAssignProductSelected(
                              e.target.checked
                                ? assignProductRows.map((r) => r.id)
                                : []
                            )
                          }
                        />
                      </th>
                      <th className="py-2 px-2">Create Date</th>
                      <th className="py-2 px-2">Logo</th>
                      <th className="py-2 px-2">Brand</th>
                      <th className="py-2 px-2">Merchant</th>
                      <th className="py-2 px-2">Product Code</th>
                      <th className="py-2 px-2">Product Name</th>
                      <th className="py-2 px-2">Shipped From</th>
                      <th className="py-2 px-2">Shipping Profiles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignProductLoading ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8">
                          <Skeleton className="h-8 w-full mb-2" />
                          <Skeleton className="h-8 w-full mb-2" />
                          <Skeleton className="h-8 w-full mb-2" />
                          <Skeleton className="h-8 w-full mb-2" />
                          <Skeleton className="h-8 w-full mb-2" />
                          <Skeleton className="h-8 w-full mb-2" />
                          <Skeleton className="h-8 w-full mb-2" />
                          <Skeleton className="h-8 w-full mb-2" />
                          <Skeleton className="h-8 w-full mb-2" />
                        </td>
                      </tr>
                    ) : assignProductRows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-xs">
                          No products found
                        </td>
                      </tr>
                    ) : (
                      assignProductRows.map((row) => (
                        <tr
                          key={row.id}
                          className="bg-white border-b hover:bg-gray-50 transition text-xs"
                        >
                          <td className="py-2 px-2">
                            <input
                              type="checkbox"
                              checked={assignProductSelected.includes(row.id)}
                              onChange={(e) => {
                                setAssignProductSelected((sel) =>
                                  e.target.checked
                                    ? [...sel, row.id]
                                    : sel.filter((id) => id !== row.id)
                                );
                                setShowAssignConfirm(false);
                              }}
                            />
                          </td>
                          <td className="py-2 px-2">
                            {getFormattedDateTime(row.createdDate) || "-"}
                          </td>
                          <td className="py-2 px-2">
                            <img
                              src={row.productImageUri || PLACEHOLDER_IMAGE}
                              alt={row.productName}
                              className="w-12 h-12 object-contain rounded border bg-gray-50"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = PLACEHOLDER_IMAGE;
                              }}
                            />
                          </td>
                          <td className="py-2 px-2">
                            {row.brandVO?.brandName || "-"}
                          </td>
                          <td className="py-2 px-2">
                            {row.redemptionMerchantVO?.merchantName || "-"}
                          </td>
                          <td className="py-2 px-2">
                            <span className="text-blue-600 underline cursor-pointer hover:text-blue-800 transition">
                              {row.productCode}
                            </span>
                          </td>
                          <td className="py-2 px-2">{row.productName}</td>
                          <td className="py-2 px-2">
                            {row.redemptionMerchantVO?.countryVO?.name || "-"}
                          </td>
                          <td className="py-2 px-2">
                            {row.redemptionMerchantVO.assignedShippingProfiles
                              ?.map((sp) => sp.name)
                              .join(", ") || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 px-6 pb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">Rows per page:</span>
                  <Select
                    value={assignProductPageSize.toString()}
                    onValueChange={(v) => {
                      setAssignProductPageSize(Number(v));
                      setAssignProductPage(0);
                    }}
                  >
                    <SelectTrigger className="w-[80px]">
                      <SelectValue
                        placeholder={assignProductPageSize.toString()}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <PaginationControls
                  totalPages={Math.ceil(
                    assignProductTotal / assignProductPageSize
                  )}
                  currentPage={assignProductPage}
                  onNextHandler={() =>
                    setAssignProductPage((p) =>
                      Math.min(
                        Math.ceil(assignProductTotal / assignProductPageSize) -
                          1,
                        p + 1
                      )
                    )
                  }
                  onPrevHandler={() =>
                    setAssignProductPage((p) => Math.max(0, p - 1))
                  }
                  onPageChange={(page) => setAssignProductPage(page)}
                />
              </div>
              {/* Assign button */}
              {assignProductSelected.length > 0 && !showAssignConfirm && (
                <div className="flex justify-end px-6 pb-2">
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => setShowAssignConfirm(true)}
                    disabled={assigning}
                  >
                    Assign
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Sub-Category Dialog */}
        <Dialog
          open={showAddSubCategoryDialog}
          onOpenChange={(v) => {
            setShowAddSubCategoryDialog(false);
            setSubCategoryName("");
            setSubCategoryCode("");
            setSubCategoryDescription("");
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Sub-Category</DialogTitle>
            </DialogHeader>
            <div className="mb-2 font-medium text-gray-700">
              Parent Category: {selectedParentCategory?.name}
            </div>
            <div className="mb-2 font-medium text-gray-700">Category Name</div>
            <Input
              placeholder="Category Name"
              value={subCategoryName}
              onChange={(e) => setSubCategoryName(e.target.value)}
              className="mb-4"
            />
            <div className="mb-2 font-medium text-gray-700">Category Code</div>
            <Input
              placeholder="Category Code"
              value={subCategoryCode}
              onChange={(e) => setSubCategoryCode(e.target.value)}
              className="mb-4"
            />
            <div className="mb-2 font-medium text-gray-700">
              Category Description
            </div>
            <Input
              placeholder="Category Description"
              value={subCategoryDescription}
              onChange={(e) => setSubCategoryDescription(e.target.value)}
              className="mb-4"
            />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddSubCategoryDialog(false);
                  setSubCategoryName("");
                  setSubCategoryCode("");
                  setSubCategoryDescription("");
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={isSubmittingSubCategory}
                onClick={async () => {
                  if (!subCategoryName.trim() || !subCategoryCode.trim()) {
                    toast.error("Category name and code are required");
                    return;
                  }

                  setIsSubmittingSubCategory(true);
                  try {
                    const payload = {
                      categoryCode: subCategoryCode.trim(),
                      categoryName: subCategoryName.trim(),
                      categoryDescription: subCategoryDescription.trim(),
                      parentCategoryId: selectedParentCategory?.id,
                      subCategoryVOs: [],
                    };

                    await PlatformCategoryApi.createPlatformCategory(payload);

                    toast.success("Sub-category created successfully");
                    setShowAddSubCategoryDialog(false);
                    setSubCategoryName("");
                    setSubCategoryCode("");
                    setSubCategoryDescription("");

                    // Refresh categories from API
                    setLoading(true);
                    try {
                      const { data } =
                        await PlatformCategoryApi.fetchCategoryTree();
                      if (data) {
                        const rootCategory = mapApiCategoryToLocal(data);
                        setCategories([rootCategory]);
                      } else {
                        setCategories([]);
                      }
                    } catch {
                      setCategories([]);
                    } finally {
                      setLoading(false);
                    }
                  } catch (error) {
                    console.error("Error creating sub-category:", error);
                    toast.error("Failed to create sub-category");
                  } finally {
                    setIsSubmittingSubCategory(false);
                  }
                }}
              >
                {isSubmittingSubCategory ? "Creating..." : "Submit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Category Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Category</DialogTitle>
            </DialogHeader>
            <div className="mb-4">
              Are you sure you want to delete the category{" "}
              <b>{categoryToDelete?.name}</b>?
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setCategoryToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDeleteCategory}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PlatformCategories;
